import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Lock, ArrowRight } from 'lucide-react';
import { Logo } from './Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'tdt_review_token';
const ADMIN_KEY = 'tdt_admin_token';
const tokenStore = window.sessionStorage; // session-scoped: clears when tab closes

/**
 * Staging gate. Unlocks if either:
 *  - a valid reviewer token is present (passcode entered on the lock screen), OR
 *  - a valid admin token is present (signed in at /admin — admin inherits reviewer access).
 */
export default function ReviewGate({ children }) {
  const [status, setStatus] = useState('checking'); // 'checking' | 'locked' | 'unlocked'
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const reviewToken = tokenStore.getItem(STORAGE_KEY);
    const adminToken = tokenStore.getItem(ADMIN_KEY);
    if (!reviewToken && !adminToken) { setStatus('locked'); return; }
    (async () => {
      try {
        if (adminToken) {
          const { data } = await axios.get(`${API}/admin/check`, {
            headers: { Authorization: `Bearer ${adminToken}` },
          });
          if (data?.unlocked) { setStatus('unlocked'); return; }
          tokenStore.removeItem(ADMIN_KEY);
        }
        if (reviewToken) {
          const { data } = await axios.get(`${API}/review/check`, {
            headers: { Authorization: `Bearer ${reviewToken}` },
          });
          if (data?.unlocked) { setStatus('unlocked'); return; }
          tokenStore.removeItem(STORAGE_KEY);
        }
        setStatus('locked');
      } catch (e) {
        tokenStore.removeItem(STORAGE_KEY);
        tokenStore.removeItem(ADMIN_KEY);
        setStatus('locked');
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) {
      setError('Please enter the review passcode.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/review/access`, { passcode: passcode.trim() });
      if (data?.token) {
        tokenStore.setItem(STORAGE_KEY, data.token);
        setStatus('unlocked');
      } else {
        setError('Unexpected response from server.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Incorrect passcode.');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'checking') {
    return (
      <div className="fixed inset-0 bg-[color:var(--tdt-ink)] flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-[color:var(--tdt-gold)] animate-pulse" />
      </div>
    );
  }

  if (status === 'locked') {
    return (
      <div
        data-testid="review-gate"
        className="fixed inset-0 z-[100] bg-[color:var(--tdt-ink)] text-[color:var(--tdt-ivory)] flex items-center justify-center px-6 overflow-hidden"
      >
        {/* ambient backdrop */}
        <img
          src="/assets/photos/private-dining.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-[color:var(--tdt-ink)]/75" />

        <div className="relative w-full max-w-md text-center">
          <div className="mb-8 sm:mb-10 inline-flex">
            <Logo size="md" tone="light" />
          </div>

          <div className="inline-flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-[color:var(--tdt-gold)]/40 text-[color:var(--tdt-gold)] mb-5 sm:mb-6">
            <Lock size={18} />
          </div>

          <div className="tdt-eyebrow tdt-eyebrow-light mb-4">Private Preview</div>
          <h1 className="font-serif font-light text-2xl sm:text-3xl lg:text-4xl leading-snug mb-4">
            Tour de Taste is in <em className="not-italic text-[color:var(--tdt-gold-soft)]">private review.</em>
          </h1>
          <p className="text-sm leading-relaxed text-[color:var(--tdt-cream)]/85 mb-8 sm:mb-10 max-w-sm mx-auto">
            This site is currently being prepared for launch. Please enter the review passcode provided to access the preview.
          </p>

          <form onSubmit={submit} noValidate className="text-left">
            <label className="block">
              <span className="tdt-label !text-[color:var(--tdt-cream)]/70">Review passcode</span>
              <input
                data-testid="gate-passcode-input"
                type="password"
                autoFocus
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Enter passcode"
                className="w-full bg-transparent border-b border-[color:var(--tdt-border-light)] focus:border-[color:var(--tdt-gold)] outline-none py-3 px-1 text-[color:var(--tdt-ivory)] placeholder:text-[color:var(--tdt-cream)]/35 tracking-[0.18em] uppercase text-center"
                style={{ letterSpacing: '0.2em' }}
              />
            </label>
            {error && (
              <p data-testid="gate-error" className="mt-4 text-xs uppercase tracking-[0.22em] text-[#e8a09a] text-center">
                {error}
              </p>
            )}
            <button
              data-testid="gate-submit"
              type="submit"
              disabled={submitting}
              className="btn-pill btn-gold mt-10 w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Verifying\u2026' : <>Enter Site <ArrowRight size={14} /></>}
            </button>
          </form>

          <p className="mt-12 text-[10px] tracking-[0.3em] uppercase text-[color:var(--tdt-cream)]/45">
            By private appointment only
          </p>
        </div>
      </div>
    );
  }

  // Unlocked
  return <>{children}</>;
}
